import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IPeriodeEvaluation, defaultValue } from 'app/shared/model/periode-evaluation.model';

export interface PeriodeEvaluationState extends EntityState<IPeriodeEvaluation> {
  entitiesByCycle: Record<string, ReadonlyArray<IPeriodeEvaluation>>;
}

const initialState: PeriodeEvaluationState = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<IPeriodeEvaluation>,
  entitiesByCycle: {} as Record<string, ReadonlyArray<IPeriodeEvaluation>>,
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
  totalItems: 0,
};

const apiUrl = 'api/periode-evaluations';

export const getPeriodes = createAsyncThunk(
  'periodeEvaluation/fetch_list',
  async ({ ecoleId, anneeScolaireId, cycleId }: { ecoleId: string; anneeScolaireId: string; cycleId: string }) => {
    const requestUrl = `${apiUrl}?ecoleId=${ecoleId}&anneeScolaireId=${anneeScolaireId}&cycleId=${cycleId}`;
    return axios.get<IPeriodeEvaluation[]>(requestUrl);
  }
);

export const getPeriode = createAsyncThunk(
  'periodeEvaluation/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IPeriodeEvaluation>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createPeriode = createAsyncThunk(
  'periodeEvaluation/create_entity',
  async (entity: IPeriodeEvaluation, thunkAPI) => {
    const result = await axios.post<IPeriodeEvaluation>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getPeriodes({ ecoleId: entity.ecoleId, anneeScolaireId: entity.anneeScolaireId, cycleId: entity.cycleId }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updatePeriode = createAsyncThunk(
  'periodeEvaluation/update_entity',
  async (entity: IPeriodeEvaluation, thunkAPI) => {
    const result = await axios.put<IPeriodeEvaluation>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getPeriodes({ ecoleId: entity.ecoleId, anneeScolaireId: entity.anneeScolaireId, cycleId: entity.cycleId }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deletePeriode = createAsyncThunk(
  'periodeEvaluation/delete_entity',
  async (entity: { id: string; ecoleId: string; anneeScolaireId: string; cycleId: string }, thunkAPI) => {
    const requestUrl = `${apiUrl}/${entity.id}`;
    const result = await axios.delete<IPeriodeEvaluation>(requestUrl);
    thunkAPI.dispatch(getPeriodes({ ecoleId: entity.ecoleId, anneeScolaireId: entity.anneeScolaireId, cycleId: entity.cycleId }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const openPeriode = createAsyncThunk(
  'periodeEvaluation/open_entity',
  async (entity: { id: string; ecoleId: string; anneeScolaireId: string; cycleId: string }, thunkAPI) => {
    const requestUrl = `${apiUrl}/${entity.id}/open`;
    const result = await axios.patch<IPeriodeEvaluation>(requestUrl);
    thunkAPI.dispatch(getPeriodes({ ecoleId: entity.ecoleId, anneeScolaireId: entity.anneeScolaireId, cycleId: entity.cycleId }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const reopenPeriode = createAsyncThunk(
  'periodeEvaluation/reopen_entity',
  async (entity: { id: string; ecoleId: string; anneeScolaireId: string; cycleId: string }, thunkAPI) => {
    const requestUrl = `${apiUrl}/${entity.id}/reopen`;
    const result = await axios.patch<IPeriodeEvaluation>(requestUrl);
    thunkAPI.dispatch(getPeriodes({ ecoleId: entity.ecoleId, anneeScolaireId: entity.anneeScolaireId, cycleId: entity.cycleId }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const closePeriode = createAsyncThunk(
  'periodeEvaluation/close_entity',
  async (entity: { id: string; ecoleId: string; anneeScolaireId: string; cycleId: string }, thunkAPI) => {
    const requestUrl = `${apiUrl}/${entity.id}/close`;
    const result = await axios.patch<IPeriodeEvaluation>(requestUrl);
    thunkAPI.dispatch(getPeriodes({ ecoleId: entity.ecoleId, anneeScolaireId: entity.anneeScolaireId, cycleId: entity.cycleId }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const PeriodeEvaluationSlice = createSlice({
  name: 'periodeEvaluation',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getPeriode.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deletePeriode.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(createPeriode, updatePeriode, openPeriode, reopenPeriode, closePeriode), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getPeriodes, getPeriode), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isFulfilled(getPeriodes), (state, action) => {
        const cycleId = action.meta.arg.cycleId;
        state.loading = false;
        state.entities = action.payload.data;
        state.entitiesByCycle[cycleId] = action.payload.data;
        state.totalItems = action.payload.data?.length || 0;
      })
      .addMatcher(isPending(createPeriode, updatePeriode, deletePeriode, openPeriode, reopenPeriode, closePeriode), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isRejected(createPeriode, updatePeriode, deletePeriode, openPeriode, reopenPeriode, closePeriode), state => {
        state.updateSuccess = false;
        state.updating = false;
        state.loading = false;
      });
  },
});

export const { reset } = PeriodeEvaluationSlice.actions;

export default PeriodeEvaluationSlice.reducer;
